import { API_SERVER_URL } from '../constants'

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }
  return null
}

/**
 * Get Facebook browser ID (fbp) from cookies
 */
function getFbp(): string | null {
  let fbp: string | null = null
  try {
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const urlParams = new URLSearchParams(window.location.search)
      fbp = urlParams.get('fbp')
    }
  } catch (e) {
    // ignore error, fallback to cookie
  }
  if (fbp) {
    return fbp
  }
  return getCookie('_fbp')
}

/**
 * Get Facebook click ID (fbc) from cookies
 */
function getFbc(): string | null {
  // First try to get from url params

  let fbc: string | null = null
  try {
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const urlParams = new URLSearchParams(window.location.search)
      fbc = urlParams.get('fbc')
    }
  } catch (e) {
    // ignore error, fallback to cookie
  }
  if (fbc) {
    return fbc
  }
  return getCookie('_fbc')
}

/**
 * Get the full URL including protocol, subdomain, domain, port, path, and query string
 */
function getFullUrl(): string {
  // window.location.href includes everything: protocol, subdomain, domain, port, path, query, hash
  // Example: https://subdomain.example.com:3000/path?query=value#hash
  return window.location.href
}

/**
 * Generate a unique event ID for deduplication between server and client tracking
 */
export function generateEventId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

export interface TrackMetaConversionParams {
  eventName: 'Lead' | 'Subscribe' | 'ViewContent' | 'PageView'
  eventId: string
  email?: string
  eventSourceUrl?: string // Optional: Full URL including subdomain. If not provided, current page URL will be used
  customData?: {
    currency?: string
    value?: number
    [key: string]: unknown
  }
}

/**
 * Track a conversion event to the Meta Conversions API via backend
 *
 * Automatically captures:
 * - Full URL with protocol, subdomain, domain, port, path, query string
 * - Facebook browser ID (fbp) and click ID (fbc) from cookies
 * - Client IP address and user agent (captured server-side)
 *
 * @param params - Conversion tracking parameters
 */
export async function trackMetaConversion(params: TrackMetaConversionParams): Promise<void> {
  const { eventName, eventId, email, eventSourceUrl, customData } = params

  try {
    const eventTime = Math.floor(Date.now() / 1000)
    const fbc = getFbc()
    const fbp = getFbp()

    // Get the full URL including subdomain if not explicitly provided
    const sourceUrl = eventSourceUrl || getFullUrl()

    const payload = {
      event_name: eventName,
      event_id: eventId,
      event_time: eventTime,
      email: email || undefined,
      fbc: fbc || undefined,
      fbp: fbp || undefined,
      event_source_url: sourceUrl,
      custom_data: customData || undefined,
    }

    const endpoint = `${API_SERVER_URL}/api/v1/public/meta-conversions/`

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      console.error('Failed to track Meta conversion:', response.statusText)
    } else {
      console.log('Successfully tracked Meta conversion:', eventName)
    }
  } catch (error) {
    console.error('Error tracking Meta conversion:', error)
  }
}
